import useAuthStore from "@Stores/AuthStore";
import { IsNullOrWhiteSpace } from "@Utils/Utils";
import axios from "axios";
import { publicIpv4 } from "public-ip";
import toast from "react-hot-toast";
import { PagingOptions } from "./internal";

const CancelToken = axios.CancelToken;
export const sourceCancelToken = CancelToken.source();

export interface HTTPServiceOptions {
    ignorePagination: boolean;
}

const instanceAxios = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL,
    responseType: "json",
    // XSRF-TOKEN : will be not working, because it's false
    withCredentials: false,

    cancelToken: sourceCancelToken.token,

    //Aborts request after 5 seconds
    // signal: controllerAbort.signal,

    // `timeout` specifies the number of milliseconds before the request times out.
    // If the request takes longer than `timeout`, the request will be aborted.
    // timeout: 1000, // default is `0` (no timeout)
});

var _urlCallback = [
    "https://icanhazip.com/",
    // "https://ifconfig.co/ip"
];
var _timeout = 1000;

instanceAxios.interceptors.request.use(
    async (req) => {
        const { isAuthorized, auth } = useAuthStore.getState();

        if (import.meta.env.PROD) {
            // skip this if is not working, else added, run in the background
            // const publicIP = await publicIp({
            const _publicIP = await publicIpv4({
                fallbackUrls: _urlCallback,
                timeout: _timeout,
            });

            if (!IsNullOrWhiteSpace(_publicIP)) {
                req.headers["X-Public-IP"] = _publicIP ?? "0.0.0.0";
            }

            // if (IsNullOrWhiteSpace(publicIP)) {
            // var IpAddress4 = await publicIpv4({
            //   fallbackUrls: _urlCallback,
            //   timeout: _timeout,
            // });

            // if (!IsNullOrWhiteSpace(IpAddress4))
            //   req.headers["X-Public-IP-v4"] = IpAddress4;

            // if (IsNullOrWhiteSpace(IpAddress4)) {
            //   var IpAddress6 = await publicIpv6({
            //     fallbackUrls: _urlCallback,
            //     timeout: _timeout,
            //   });

            //   if (!IsNullOrWhiteSpace(IpAddress6))
            //     req.headers["X-Public-IP-v6"] = IpAddress6;
            // }
            // }
        }

        if (isAuthorized) {
            req.headers.Authorization = `Bearer ${auth?.accessToken}`;
        }

        return req;
    },
    (err) => {
        import.meta.env.DEV && console.log("Request Error", err);

        return Promise.reject(err);
        // return err;
    },
);

instanceAxios.interceptors.response.use(
    (res) => {
        if (res.status === 201) {
            if (res.data?.message)
                toast.success(res.data?.message.capitalize());
            else
                toast.success(
                    "New Record has been added successfully".capitalize(),
                );
        }

        if (res.status === 204) {
            if (res.data?.message)
                toast.success(res.data?.message.capitalize());
            else
                toast.success(
                    "Record has been updated successfully".capitalize(),
                );
        }

        if (res.status === 200 && res.data?.message)
            toast.success(res.data?.message.capitalize());

        return res;
    },
    (error) => {
        if (axios.isAxiosError(error)) {
            const { response } = error;

            const { logOut } = useAuthStore.getState();

            if (response?.status === 403) {
                sourceCancelToken.cancel();

                if (import.meta.env.PROD) {
                    logOut();
                }

                toast.error(
                    (response?.data?.message?.capitalize() ||
                        response.data?.detail?.capitalize() ||
                        response?.data?.title?.capitalize()) ??
                        "You are not allowed to access".capitalize(),
                );
            }

            if (response?.status === 400) {
                if (response?.data?.message || response?.data?.title)
                    toast.error(
                        (response?.data?.message?.capitalize() ||
                            response?.data?.title?.capitalize()) ??
                            "An unprecedented error occurred, please try again.".capitalize(),
                    );
            }

            if (error.response?.status === 404) {
                toast.error("Oops \nRequested object not found".capitalize());
            }

            if (error.response?.status === 401) {
                if (error?.response?.data == undefined) {
                    logOut();
                    // NOTE: when the return 401, when the user is trailing to access the unauthorized page, it will be canceled, otherwise, it will not be canceled
                    sourceCancelToken.cancel();

                    return error;
                }
                // else

                toast.error(
                    response?.data?.message?.capitalize() ??
                        "Oops, Your Session has expired, login again".capitalize(),
                );
            }

            if (error.response?.status === 500) {
                // sourceCancelToken.cancel();

                toast.error(
                    "An unprecedented error occurred, please try again.".capitalize(),
                );
            }
        }

        return Promise.reject(error);
    },
);

export const httpService = <T>(
    endpoint: string,
    options?: PagingOptions | URLSearchParams | string | undefined,
    // responseType?: ResponseType | undefined,
    serviceOptions?: HTTPServiceOptions,
) => {
    const query =
        options ??
        (serviceOptions?.ignorePagination
            ? new PagingOptions(1, -1)
            : new PagingOptions());

    const formattedQuery =
        query instanceof PagingOptions
            ? query.format()
            : query instanceof URLSearchParams
              ? query.toString()
              : query;

    return {
        getAll: () =>
            IsNullOrWhiteSpace(formattedQuery)
                ? instanceAxios.get(endpoint)
                : instanceAxios.get(endpoint + `?${formattedQuery}`),

        getById: (id: number | string) =>
            IsNullOrWhiteSpace(formattedQuery)
                ? instanceAxios.get(endpoint + "/" + id)
                : instanceAxios.get(endpoint + "/" + id + `?${formattedQuery}`),

        post: (newRecord: T) => instanceAxios.post(endpoint, newRecord),

        update: (id: number | string, updatedRecord: T) =>
            instanceAxios.put(endpoint + "/" + id, updatedRecord),

        delete: (id: number | string) =>
            instanceAxios.delete(endpoint + "/" + id),
    };
};

// cancel the request (the message parameter is optional)
// sourceCancelToken.cancel("Operation canceled by the user.");

// OR
// controllerAbort.abort(); // the message parameter is not supported

export default instanceAxios;
